import { Eye, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Advertisement, AdvertisementType } from "@/client";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useDeleteAdvertisement } from "../api/delete-advertisement";
import {
  advertisementsQueryKey,
  useAdvertisements,
} from "../api/get-advertisements";

import { CreateAdvertisementDialog } from "./create-advertisement-dialog";
import { DeleteAdvertisementDialog } from "./delete-advertisement-dialog";

export function AdvertisementsList() {
  const { t } = useTranslation();
  const { congress } = useCongress();
  const queryClient = useQueryClient();
  const {
    data: advertisements,
    isLoading,
    isSuccess,
  } = useAdvertisements({
    congressId: congress.id,
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deletingAdvertisement, setDeletingAdvertisement] =
    useState<Advertisement | null>(null);

  const { mutate: deleteAdvertisement } = useDeleteAdvertisement({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: advertisementsQueryKey(congress?.id),
      });
      setDeletingAdvertisement(null);
    },
  });

  const getTypeLabel = (type: Advertisement["type"]) => {
    switch (type) {
      case AdvertisementType.SPLASH_SCREEN:
        return t("ads.splashScreen");
      default:
        return type;
    }
  };

  const handleViewImage = (advertisement: Advertisement) => {
    if (advertisement.image?.file_name) {
      window.open(
        `${import.meta.env.VITE_BUCKET}/${advertisement.image.file_name}`,
        "_blank",
      );
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isSuccess) {
    return <div>{t("ads.loadError")}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("ads.title")}</h1>
        <Button
          onClick={() => setIsCreateOpen(true)}
          disabled={advertisements.length >= 1}
        >
          <Plus className="mr-2 size-4" />
          {t("ads.add")}
        </Button>
      </div>

      <Table className="rounded-md border">
        <TableHeader>
          <TableRow>
            <TableHead>{t("ads.name")}</TableHead>
            <TableHead>{t("ads.type")}</TableHead>
            <TableHead>{t("ads.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {advertisements.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-8 text-center">
                {t("ads.empty")}
              </TableCell>
            </TableRow>
          ) : (
            advertisements.map((advertisement) => (
              <TableRow key={advertisement.id}>
                <TableCell>{advertisement.name}</TableCell>
                <TableCell>{getTypeLabel(advertisement.type)}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewImage(advertisement)}
                    >
                      <Eye className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingAdvertisement(advertisement)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <CreateAdvertisementDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
      />

      <DeleteAdvertisementDialog
        advertisement={deletingAdvertisement}
        open={!!deletingAdvertisement}
        onOpenChange={(open) => !open && setDeletingAdvertisement(null)}
        onConfirm={() =>
          deletingAdvertisement &&
          deleteAdvertisement({ id: deletingAdvertisement.id })
        }
      />
    </div>
  );
}
